import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setIsLoginDialog } from '../../redux/reducers/loginSlice';
import { FileText, Shield, Users, CreditCard, Video, BarChart2, AlertTriangle, Lock, Trash2, RefreshCw, Scale, Mail } from 'lucide-react';
import ContactFormModal from '../ContactForm/ContactFormModal';

const TermsAndConditions = () => {
    const dispatch = useDispatch();
    const [activeSection, setActiveSection] = useState('');
    const [openContactForm, setOpenContactForm] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(setIsLoginDialog(false));

        document.documentElement.style.scrollBehavior = 'smooth';

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0px -80% 0px' }
        );

        document.querySelectorAll('section[id]').forEach((section) => {
            observer.observe(section);
        });

        return () => {
            document.documentElement.style.scrollBehavior = '';
            observer.disconnect();
        };
    }, [dispatch]);

    const sections = [
        { id: 'section-1',  title: '1. Acceptance of Terms',    icon: FileText },
        { id: 'section-2',  title: '2. Services',               icon: BarChart2 },
        { id: 'section-3',  title: '3. User Responsibilities',  icon: Users },
        { id: 'section-4',  title: '4. Video Upload & Content', icon: Video },
        { id: 'section-5',  title: '5. Subscription & Usage',   icon: CreditCard },
        { id: 'section-6',  title: '6. Payments',               icon: CreditCard },
        { id: 'section-7',  title: '7. No Guarantee of Results',icon: AlertTriangle },
        { id: 'section-8',  title: '8. Limitation of Liability',icon: Shield },
        { id: 'section-9',  title: '9. Intellectual Property',  icon: Lock },
        { id: 'section-10', title: '10. Termination',           icon: Trash2 },
        { id: 'section-11', title: '11. Modifications',         icon: RefreshCw },
        { id: 'section-12', title: '12. Governing Law',         icon: Scale },
        { id: 'section-13', title: '13. Contact',               icon: Mail },
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    const sectionData = [
        {
            id: 'section-1',
            icon: FileText,
            title: '1. Acceptance of Terms',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    By using VISIST.AI, you agree to these Terms & Conditions. If you do not agree with any part of these Terms, please do not use the platform.
                </p>
            ),
        },
        {
            id: 'section-2',
            icon: BarChart2,
            title: '2. Services',
            content: (
                <>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        VISIST.AI provides AI-powered sports analysis tools including:
                    </p>
                    <ul className="space-y-3">
                        {[
                            { label: 'Smart Journal', desc: 'Video-based performance tracking' },
                            { label: 'Smart Insights', desc: 'Match analytics powered by AI' },
                            { label: 'Smart Assessments', desc: 'Skill evaluation for academies' },
                        ].map((item) => (
                            <li key={item.label} className="flex gap-3">
                                <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                <div>
                                    <strong className="text-gray-900">{item.label}:</strong>
                                    <span className="text-gray-700"> {item.desc}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            ),
        },
        {
            id: 'section-3',
            icon: Users,
            title: '3. User Responsibilities',
            content: (
                <ul className="space-y-3">
                    {[
                        'You must provide accurate information during registration.',
                        'You are responsible for maintaining the confidentiality of your login credentials.',
                        'You agree to use the platform only for lawful purposes.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-4',
            icon: Video,
            title: '4. Video Upload & Content',
            content: (
                <ul className="space-y-3">
                    {[
                        'Users retain ownership of uploaded videos.',
                        'By uploading, you grant VISIST.AI permission to process videos for analysis purposes.',
                        'Users must ensure they have obtained consent to upload videos of athletes, especially minors.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-5',
            icon: CreditCard,
            title: '5. Subscription & Usage',
            content: (
                <ul className="space-y-3">
                    {[
                        'Plans include limits on the number of videos or assessments as per the selected package.',
                        'Validity periods apply and unused credits expire after the validity period ends.',
                        'Subscriptions may auto-renew depending on the selected plan.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-6',
            icon: CreditCard,
            title: '6. Payments',
            content: (
                <ul className="space-y-3">
                    {[
                        'All payments must be made in advance.',
                        'Prices may vary by geography (India / International).',
                        'Applicable taxes may be added at checkout.',
                    ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                            <span className="text-gray-700">{item}</span>
                        </li>
                    ))}
                </ul>
            ),
        },
        {
            id: 'section-7',
            icon: AlertTriangle,
            title: '7. No Guarantee of Results',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    VISIST.AI provides analytical insights to support training and performance improvement. We do not guarantee specific performance outcomes or results from the use of our platform.
                </p>
            ),
        },
        {
            id: 'section-8',
            icon: Shield,
            title: '8. Limitation of Liability',
            content: (
                <>
                    <p className="text-gray-700 leading-relaxed mb-4">VISIST.AI is not liable for:</p>
                    <ul className="space-y-3">
                        {[
                            'Injury, training decisions, or performance outcomes resulting from the use of our analysis.',
                            'Loss of data due to external factors beyond our reasonable control.',
                            'Service interruptions beyond our reasonable control.',
                        ].map((item, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="font-bold flex-shrink-0" style={{ color: '#247E45' }}>•</span>
                                <span className="text-gray-700">{item}</span>
                            </li>
                        ))}
                    </ul>
                </>
            ),
        },
        {
            id: 'section-9',
            icon: Lock,
            title: '9. Intellectual Property',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    All platform technology, analytics models, generated reports, and associated content are the exclusive intellectual property of VISIST.AI. Reproduction or redistribution without prior written consent is strictly prohibited.
                </p>
            ),
        },
        {
            id: 'section-10',
            icon: Trash2,
            title: '10. Termination',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    VISIST.AI reserves the right to suspend or terminate user accounts in the event of misuse, violation of these Terms, or any activity that undermines the integrity or security of the platform.
                </p>
            ),
        },
        {
            id: 'section-11',
            icon: RefreshCw,
            title: '11. Modifications',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    We may update these Terms & Conditions from time to time to reflect changes in our services or applicable laws. Continued use of the platform after any such update implies your acceptance of the revised Terms.
                </p>
            ),
        },
        {
            id: 'section-12',
            icon: Scale,
            title: '12. Governing Law',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    These Terms & Conditions are governed by the laws of India. Any disputes arising from the use of the platform shall be subject to the exclusive jurisdiction of the courts in <strong>Bangalore, Karnataka</strong>.
                </p>
            ),
        },
        {
            id: 'section-13',
            icon: Mail,
            title: '13. Contact',
            content: (
                <p className="text-gray-700 leading-relaxed">
                    For any questions or concerns regarding these Terms & Conditions, please contact us at:{' '}
                    <a
                        href="mailto:sales@visist.ai"
                        className="font-semibold no-underline hover:underline"
                        style={{ color: '#247E45' }}
                    >
                        sales@visist.ai
                    </a>
                </p>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-deep-charcoal text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-deep-charcoal to-gray-900"></div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-6 py-20 md:py-32 relative z-10">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
                            <FileText className="w-4 h-4 text-emerald" />
                            <span className="text-sm font-semibold text-white">Legal Agreement</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight uppercase text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">
                            Terms &amp; Conditions
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed max-w-3xl">
                            These Terms &amp; Conditions govern your use of VISIST.AI services, including Smart Journal, Smart Insights, and Smart Assessments.
                        </p>
                        <p className="mt-6 text-gray-400 text-sm">
                            Last updated: <span className="font-semibold text-white">April 2026</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-12 md:py-20">
                <div className="flex flex-col lg:flex-row gap-12 max-w-7xl mx-auto">
                    {/* Sticky Table of Contents */}
                    <aside className="lg:w-80 flex-shrink-0">
                        <div className="lg:sticky lg:top-24 space-y-6">
                            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-6">
                                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5" style={{ color: '#247E45' }} />
                                    Table of Contents
                                </h2>
                                <nav className="space-y-1">
                                    {sections.map((section) => {
                                        const Icon = section.icon;
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => scrollToSection(section.id)}
                                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-3 group ${activeSection === section.id
                                                    ? 'font-semibold'
                                                    : 'text-slate-600 hover:bg-gray-50 hover:text-gray-900'
                                                    }`}
                                                style={activeSection === section.id ? { backgroundColor: '#D9F0DE', color: '#026B33' } : {}}
                                            >
                                                <Icon
                                                    className="w-4 h-4 flex-shrink-0"
                                                    style={{ color: activeSection === section.id ? '#026B33' : '#9CA3AF' }}
                                                />
                                                <span className="text-sm leading-tight">{section.title}</span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            {/* Quick Contact Card */}
                            <div className="rounded-2xl p-6 border-2" style={{ background: 'linear-gradient(to bottom right, #D9F0DE, #E8FFE8)', borderColor: '#247E45' }}>
                                <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#026B33' }}>
                                    <Mail className="w-5 h-5" />
                                    Questions?
                                </h3>
                                <p className="text-sm mb-3" style={{ color: '#026B33' }}>
                                    Contact us about these Terms &amp; Conditions
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 text-sm font-semibold hover:underline transition-colors"
                                    style={{ color: '#026B33' }}
                                >
                                    Click Here
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Content */}
                    <main className="flex-1 min-w-0">
                        {/* Introduction */}
                        <div className="prose prose-slate max-w-none mb-12">
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10">
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    These Terms &amp; Conditions govern your use of{' '}
                                    <a href="https://www.visist.ai" target="_blank" rel="noopener noreferrer" className="font-semibold no-underline hover:underline" style={{ color: '#247E45' }}>
                                        www.visist.ai
                                    </a>{' '}
                                    ("we," "us," or "our"), including all associated services.
                                </p>
                                <p className="text-gray-600 leading-relaxed">
                                    By accessing or using the platform, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree, please do not use the platform.
                                </p>
                            </div>
                        </div>

                        {/* Section Cards */}
                        {sectionData.map((section) => {
                            const Icon = section.icon;
                            return (
                                <section key={section.id} id={section.id} className="mb-12 scroll-mt-24">
                                    <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-200 p-8 md:p-10 hover:border-emerald transition-all duration-300">
                                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D9F0DE' }}>
                                                <Icon className="w-5 h-5" style={{ color: '#247E45' }} />
                                            </div>
                                            {section.title}
                                        </h2>
                                        {section.content}
                                    </div>
                                </section>
                            );
                        })}

                        {/* Footer CTA */}
                        <div className="rounded-2xl p-8 md:p-10 text-white text-center relative overflow-hidden bg-deep-charcoal">
                            <div className="absolute inset-0 bg-gradient-to-br from-deep-charcoal via-gray-900 to-deep-charcoal"></div>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald/10 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold mb-3 text-white">Still Have Questions?</h3>
                                <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                                    We're here to help. Reach out to us if you need clarification on any part of these Terms &amp; Conditions.
                                </p>
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                                    style={{ color: '#026B33' }}
                                >
                                    <Mail className="w-5 h-5" />
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />
        </div>
    );
};

export default TermsAndConditions;